import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Instagram } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "./Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EVENT_TYPES = ["Wedding", "Corporate Event", "Birthday Party", "Anniversary", "Exhibition", "Other"];
const BUDGETS = ["Under ₹50,000", "₹50,000 – ₹1,50,000", "₹1,50,000 – ₹5,00,000", "₹5,00,000+"];
const empty = { name: "", phone: "", email: "", event_type: "", event_date: "", budget: "", message: "" };

export default function Inquiry() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.event_type) {
      toast.error("Please fill in your name, phone and event type.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Thank you! Our team will reach out to you shortly.");
      setForm(empty);
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="inquiry" data-testid="inquiry-section" className="bg-heirloom-bone py-24 md:py-28">
      <div className="max-w-[1300px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 rounded-3xl overflow-hidden shadow-[0_30px_80px_-40px_rgba(0,0,0,0.4)] border border-heirloom-line">
          {/* Left info panel */}
          <div className="lg:col-span-5 bg-heirloom-maroon text-heirloom-bone p-10 md:p-12 relative overflow-hidden">
            <div className="absolute -bottom-16 -right-16 h-56 w-56 rounded-full bg-heirloom-maroon-deep/60" />
            <div className="relative z-10">
              <span className="font-script text-3xl text-heirloom-gold-light">Get In Touch</span>
              <h2 className="font-display text-3xl md:text-4xl mt-2 leading-tight">Let&rsquo;s Plan Your Perfect Celebration</h2>
              <p className="text-heirloom-bone/80 mt-5 leading-relaxed text-sm">
                Share a few details and our team will craft a bespoke proposal — from intimate gatherings to grand royal weddings.
              </p>
              <div className="mt-10 space-y-5">
                <a href="tel:+918770847087" className="flex items-center gap-4 group">
                  <span className="h-11 w-11 rounded-full bg-heirloom-bone/10 flex items-center justify-center group-hover:bg-heirloom-gold group-hover:text-heirloom-maroon-deep transition-colors"><Phone size={18} /></span>
                  <div><div className="overline text-[0.55rem] text-heirloom-bone/60">Call Us</div><div className="text-sm">+91 87708 47087 &middot; +91 93200 18909</div></div>
                </a>
                <a href="mailto:arihanteventsindore@gmail.com" className="flex items-center gap-4 group">
                  <span className="h-11 w-11 rounded-full bg-heirloom-bone/10 flex items-center justify-center group-hover:bg-heirloom-gold group-hover:text-heirloom-maroon-deep transition-colors"><Mail size={18} /></span>
                  <div><div className="overline text-[0.55rem] text-heirloom-bone/60">Email</div><div className="text-sm break-all">arihanteventsindore@gmail.com</div></div>
                </a>
                <div className="flex items-center gap-4">
                  <span className="h-11 w-11 rounded-full bg-heirloom-bone/10 flex items-center justify-center"><MapPin size={18} /></span>
                  <div><div className="overline text-[0.55rem] text-heirloom-bone/60">Visit</div><div className="text-sm">Orbit Mall, Vijay Nagar, Indore</div></div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="h-11 w-11 rounded-full bg-heirloom-bone/10 flex items-center justify-center"><Instagram size={18} /></span>
                  <div><div className="overline text-[0.55rem] text-heirloom-bone/60">Follow</div><div className="text-sm">@arihantevent</div></div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 bg-heirloom-bone p-10 md:p-12">
            <form onSubmit={submit} data-testid="inquiry-form" className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="overline text-[0.6rem] text-heirloom-obsidian/60 block mb-1.5">Full Name *</label>
                <input data-testid="inquiry-name" className="box-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
              </div>
              <div>
                <label className="overline text-[0.6rem] text-heirloom-obsidian/60 block mb-1.5">Phone *</label>
                <input data-testid="inquiry-phone" className="box-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91" />
              </div>
              <div>
                <label className="overline text-[0.6rem] text-heirloom-obsidian/60 block mb-1.5">Email</label>
                <input data-testid="inquiry-email" type="email" className="box-input" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
              </div>
              <div>
                <label className="overline text-[0.6rem] text-heirloom-obsidian/60 block mb-1.5">Event Date</label>
                <input data-testid="inquiry-date" type="date" className="box-input" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} />
              </div>
              <div>
                <label className="overline text-[0.6rem] text-heirloom-obsidian/60 block mb-1.5">Event Type *</label>
                <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
                  <SelectTrigger data-testid="inquiry-event-type" className="box-input h-auto"><SelectValue placeholder="Select event" /></SelectTrigger>
                  <SelectContent>{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="overline text-[0.6rem] text-heirloom-obsidian/60 block mb-1.5">Budget</label>
                <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                  <SelectTrigger data-testid="inquiry-budget" className="box-input h-auto"><SelectValue placeholder="Select budget" /></SelectTrigger>
                  <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className="overline text-[0.6rem] text-heirloom-obsidian/60 block mb-1.5">Tell us more</label>
                <textarea data-testid="inquiry-message" rows={3} className="box-input resize-none" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Guests, venue, vision…" />
              </div>
              <div className="sm:col-span-2">
                <button type="submit" data-testid="inquiry-submit-btn" disabled={loading} className="w-full sm:w-auto bg-heirloom-maroon text-heirloom-bone text-xs font-semibold tracking-[0.18em] uppercase px-10 py-4 rounded-full hover:bg-heirloom-maroon-deep transition-colors disabled:opacity-60">
                  {loading ? "Sending…" : "Send Inquiry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
